import { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';

import './limits.scss';

import { updateLimitsBySection } from '~modules/limits/api';
import { LimitDto } from '~modules/limits/dto';
import { SectionDto } from '~modules/section/model';
import { useLoadSections } from '~pages/cabinet/admin/sections/SectionList/use-load-sections';
import rightIcon from '~shared/assets/icons/right.svg';
import searchIcon from '~shared/assets/icons/search.svg';
import { Preloader, toastError, toastSuccess } from '~shared/ui';
import { Breadcrumbs } from '~shared/ui/breadcrumbs/breadcrumbs';
import { SaveBtn } from '~shared/ui/button/save-btn';
import { InputItem } from '~shared/ui/input-item/input-item';
import { HeaderFilterPanel } from '~shared/ui/new-table/header-filter-panel/header-filter-panel';
import NewTable, { INewTableColumn } from '~shared/ui/new-table/new-table';

import { useLimits } from './use-limits';

export function LimitsPage() {
	const { list: sections, isPending } = useLoadSections();
	const [sectionId, setSectionId] = useState<number>();

	const { list: limits, pending: isPendingRoles } = useLimits(sectionId);

	const [data, setData] = useState<LimitDto[]>([]);

	useEffect(() => {
		setData(limits);
	}, [limits]);

	const handleRowChange = useCallback((updates: Partial<LimitDto>, idx: number) => {
		setData(prevState => prevState.map((limit, i) => (i === idx ? { ...limit, ...updates } : { ...limit })));
	}, []);

	const handleSectionChange = useCallback((selectedSection: SectionDto | null) => {
		setSectionId(selectedSection?.id ?? undefined);
	}, []);

	const renderEditableCell = useCallback(
		(field: 'full_time_work' | 'overtime' | 'part_time_job') => (row: LimitDto, rowIndex: number) => (
			<InputItem
				placeholder=""
				value={row[field]}
				onChange={event => handleRowChange({ [field]: Number(event.target.value) }, rowIndex)}
			/>
		),
		[handleRowChange],
	);

	const columns: INewTableColumn<LimitDto>[] = [
		{
			key: 'position',
			title: 'Должность',
			render: row => row.staffing?.name ?? '-',
		},
		{
			key: 'full_time_work',
			title: 'Кол-во допустимых часов на работу',
			render: renderEditableCell('full_time_work'),
		},
		{
			key: 'overtime',
			title: 'Допустимый процент на переработку',
			render: renderEditableCell('overtime'),
		},
		{
			key: 'part_time_job',
			title: 'Допустимый процент на подработку',
			render: renderEditableCell('part_time_job'),
		},
	];

	const handleSave = async () => {
		if (!sectionId) return;
		try {
			if (sectionId)
				updateLimitsBySection(
					sectionId,
					data.map(v => ({
						position_id: v.staffing!.position_id,
						full_time_work: v.full_time_work,
						overtime: v.overtime,
						part_time_job: v.part_time_job,
					})),
				);
			toastSuccess('Лимиты обновлены');
		} catch (err) {
			if (err instanceof Error) {
				toastError(err.message);
			} else console.error(err);
		}
	};

	return (
		<div className="limits-page page">
			<div className="page-container">
				<Breadcrumbs
					className="attendance-breadcrumbs"
					separatorIconSrc={rightIcon}
					items={[{ label: 'Табелирование' }, { label: 'Лимиты рабочих часов' }]}
				/>
				{isPending ? (
					<Preloader />
				) : (
					<>
						<h2 className="page-title" style={{ marginBottom: '40px' }}>
							Лимиты рабочих часов
						</h2>
						<HeaderFilterPanel
							className="limits-filters"
							columnsTemplate="minmax(320px, 1fr) minmax(320px, 1fr)"
						>
							<div className="header-filter-panel__field">
								<label className="header-filter-panel__label" htmlFor="section-select">
									Участок
								</label>
								<div className="header-filter-panel__input-with-icon header-filter-panel__input-with-icon--left">
									<span className="header-filter-panel__input-icon" aria-hidden={true}>
										<img src={searchIcon} alt="" />
									</span>
									<Select<SectionDto>
										inputId="section-select"
										className="react-select-container"
										classNamePrefix="react-select"
										getOptionLabel={option => `${option.title}`}
										getOptionValue={option => `${option.id}`}
										options={sections}
										isClearable={true}
										onChange={handleSectionChange}
										placeholder="Выберите участок..."
										noOptionsMessage={() => 'Нет доступных участков'}
									/>
								</div>
							</div>
						</HeaderFilterPanel>

						<NewTable<LimitDto>
							columns={columns}
							rows={data}
							pending={isPendingRoles}
							emptyText="Нет данных"
						/>
						<SaveBtn
							style={{ margin: '20px auto 255px auto' }}
							handleSave={handleSave}
							disabled={!sectionId}
						/>
					</>
				)}
			</div>
		</div>
	);
}
